import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    ButtonGroup,
    Chip,
    CircularProgress,
    Container,
    LinearProgress,
    Stack,
    Switch,
    FormControlLabel,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import AddIcon from '@mui/icons-material/Add';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { apiFetch } from '../lib/api';
import { useCalendar, eventCellKey, type CalendarAssignment, type CalendarEvent } from '../hooks/useCalendar';
import { JobGrid } from '../components/calendar/JobGrid';
import { StaffPicker, type PickerTarget } from '../components/calendar/StaffPicker';
import { Toast, type ToastMessage } from '../components/Toast';
import { EventDialog, type EventDraft } from '../components/calendar/EventDialog';
import {
    QUICK_RANGES,
    addDays,
    endOfMonth,
    formatMonthYear,
    formatShort,
    rangeOf,
    startOfMonth,
    startOfWeek,
    today as todayIso,
    type IsoDate,
} from '../lib/dates';

/**
 * Calendar — the scheduling screen.
 *
 * Jobs down the side, days across the top, crew in the cells. Three spans,
 * because the office works at three zoom levels: this week for today's crews, a
 * fortnight for the pay cycle, a month for planning. All three are the same
 * grid and the same two requests; only the number of columns changes.
 */

type SpanId = 'week' | 'fortnight' | 'month';

const SPANS: readonly { id: SpanId; label: string }[] = [
    { id: 'week', label: 'Week' },
    { id: 'fortnight', label: 'Fortnight' },
    { id: 'month', label: 'Month' },
];

function rangeFor(span: SpanId, cursor: IsoDate): { from: IsoDate; to: IsoDate } {
    if (span === 'month') {
        return { from: startOfMonth(cursor), to: endOfMonth(cursor) };
    }
    const from = startOfWeek(cursor);
    return { from, to: addDays(from, (span === 'week' ? 7 : 14) - 1) };
}

export function Calendar(): React.JSX.Element {
    const today = useMemo(() => todayIso(), []);
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
    /**
     * A phone, either way up: a touch screen whose short side is under 600px.
     * The board is view-only there — see JobGrid's readOnly. Tablets and
     * laptops are not caught by this and edit as normal.
     */
    const readOnly = useMediaQuery(
        '(pointer: coarse) and (max-width: 599.95px), (pointer: coarse) and (max-height: 599.95px)',
        { noSsr: true },
    );
    // A fortnight is fourteen columns; on a phone that is a lot of sideways
    // scrolling before you see anything, so a phone opens on the week.
    const [span, setSpan] = useState<SpanId>(() => (isPhone ? 'week' : 'fortnight'));
    const [cursor, setCursor] = useState<IsoDate>(today);
    const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());
    const [pickerOpen, setPickerOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    /** The assignment behind a "Removed" toast, so its Undo can put it back. */
    const [undoable, setUndoable] = useState<{ assignment: CalendarAssignment; message: string } | null>(null);
    /** Only while that toast is the one showing — a later "Moved" must not offer to undo a removal. */
    const canUndo = undoable !== null && toast?.message === undoable.message;
    const [filter, setFilter] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    /** null while creating a new job. */
    const [editing, setEditing] = useState<CalendarEvent | null>(null);
    const [dialogError, setDialogError] = useState<string | null>(null);
    /** Set by a quick-range button; overrides the span arithmetic until the span changes. */
    const [override, setOverride] = useState<{ from: IsoDate; to: IsoDate } | null>(null);

    const { from, to } = useMemo(() => override ?? rangeFor(span, cursor), [override, span, cursor]);
    const dates = useMemo(() => rangeOf(from, to), [from, to]);

    const { range, staff, categories, eventsById, staffById, cells, loading, initialising, error, reload } =
        useCalendar(from, to, showArchived);

    useEffect(() => {
        function onKey(e: KeyboardEvent): void {
            if (e.key === 'Escape') {
                setSelection(new Set());
                setPickerOpen(false);
            }
        }
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
        };
    }, []);

    // Paging resets the selection: cells are keyed by job and date, so keeping
    // them would silently target days that are no longer on screen.
    const step = useCallback(
        (direction: -1 | 1) => {
            setSelection(new Set());
            setOverride(null);
            setCursor((c) => {
                if (span === 'month') {
                    const d = startOfMonth(c);
                    return direction === 1 ? addDays(endOfMonth(d), 1) : startOfMonth(addDays(d, -1));
                }
                return addDays(c, direction * (span === 'week' ? 7 : 14));
            });
        },
        [span],
    );

    /** Category id -> its position in the API's ordering (sort_order, then name). */
    const categoryRank = useMemo(() => {
        const m = new Map<number, number>();
        categories.forEach((c, i) => m.set(c.id, i));
        return m;
    }, [categories]);

    const visibleEvents = useMemo(() => {
        const all = range?.events ?? [];
        const q = filter.trim().toLowerCase();
        const matched =
            q === ''
                ? all
                : all.filter((e) =>
                      [e.siteAddress, e.jobNumber, e.clientName].some((v) => (v ?? '').toLowerCase().includes(q)),
                  );

        /*
         * Jobs first, then everything that is not a job — Annual Leave, Sick
         * Leave, Trade School, Public Holiday and so on — under an "Other"
         * heading at the bottom.
         *
         * These rows are not work: nobody is scheduling a client site when they
         * open the calendar and find Annual Leave sorted between two addresses.
         * Within "Other" the rows follow the order the API returned the
         * categories in, which is calendar_category.sort_order then name — so
         * the admin controls it, rather than it being an accident of spelling or
         * of which category happened to be created first.
         */
        return [...matched].sort((a, b) => {
            const aCat = a.categoryId;
            const bCat = b.categoryId;
            if (aCat === null && bCat === null) {
                return 0;
            }
            if (aCat === null) {
                return -1;
            }
            if (bCat === null) {
                return 1;
            }
            const aRank = categoryRank.get(aCat) ?? Number.MAX_SAFE_INTEGER;
            const bRank = categoryRank.get(bCat) ?? Number.MAX_SAFE_INTEGER;
            return aRank - bRank || a.id - b.id;
        });
    }, [range, filter, categoryRank]);

    const categoryNames = useMemo(() => {
        const m = new Map<number, string>();
        for (const c of categories) {
            m.set(c.id, c.name);
        }
        return m;
    }, [categories]);

    /**
     * The selection, resolved to one job and its days.
     *
     * A drag can cross job rows, but a single "add staff" action belongs to one
     * job — so a multi-job selection assigns to the first row touched, and the
     * panel names it. Silently writing to several jobs at once is not something
     * a scheduler would expect from one button.
     */
    const target: PickerTarget | null = useMemo(() => {
        if (selection.size === 0) {
            return null;
        }
        const days = new Set<IsoDate>();
        let eventId: number | null = null;
        for (const key of selection) {
            const [e, d] = key.split('|');
            if (e === undefined || d === undefined) {
                continue;
            }
            const id = Number(e);
            eventId ??= id;
            if (id === eventId) {
                days.add(d);
            }
        }
        if (eventId === null) {
            return null;
        }
        const event: CalendarEvent | undefined = eventsById.get(eventId);
        if (event === undefined) {
            return null;
        }
        return { event, dates: [...days].sort(), cellCount: selection.size };
    }, [selection, eventsById]);

    const handleSubmit = useCallback(
        (
            staffIds: readonly number[],
            shift: { start: string | null; end: string | null },
            dates: readonly IsoDate[],
            removeIds: readonly number[],
        ) => {
            if (target === null) {
                return;
            }
            setSaving(true);
            setSaveError(null);

            async function run(eventId: number): Promise<ToastMessage> {
                const parts: string[] = [];
                let warn = false;
                if (staffIds.length > 0) {
                    const result = await apiFetch<{ inserted: number; skipped: number; moved: number }>(
                        '/calendar/assignments',
                        {
                            method: 'POST',
                            body: JSON.stringify({
                                // Adding someone already on another job that day
                                // moves them here; the panel says so beside them.
                                moveFromOtherJobs: true,
                                eventId,
                                staffIds,
                                dates,
                                startTime: shift.start,
                                endTime: shift.end,
                            }),
                        },
                    );
                    parts.push(`Added ${String(result.inserted)}`);
                    if (result.skipped > 0) {
                        parts.push(`skipped ${String(result.skipped)} already rostered`);
                        warn = result.inserted === 0;
                    }
                    if (result.moved > 0) {
                        parts.push(`moved ${String(result.moved)} from other bookings`);
                    }
                }
                if (removeIds.length > 0) {
                    const result = await apiFetch<{ deleted: number }>('/calendar/assignments', {
                        method: 'DELETE',
                        body: JSON.stringify({ ids: removeIds }),
                    });
                    parts.push(`removed ${String(result.deleted)}`);
                    warn = false;
                }
                const message = parts.join(' · ');
                return {
                    message: message.charAt(0).toUpperCase() + message.slice(1),
                    severity: warn ? 'warning' : 'success',
                };
            }

            run(target.event.id)
                .then((toastMessage) => {
                    setToast(toastMessage);
                    setPickerOpen(false);
                    setSelection(new Set());
                    reload();
                })
                .catch((err: unknown) => {
                    setSaveError(err instanceof Error ? err.message : 'Could not save');
                    // Part of it may have gone through before the failure.
                    reload();
                })
                .finally(() => {
                    setSaving(false);
                });
        },
        [target, reload],
    );

    /** The x on a staff chip takes that person off that job for that day. */
    const handleRemoveAssignment = useCallback(
        (a: CalendarAssignment) => {
            setSaving(true);
            apiFetch<{ deleted: number }>('/calendar/assignments', {
                method: 'DELETE',
                body: JSON.stringify({ ids: [a.id] }),
            })
                .then(() => {
                    const name = a.staffId === null ? 'Unfilled slot' : (staffById.get(a.staffId)?.name ?? 'Staff');
                    const message = `Removed ${name}`;
                    setToast({ message, severity: 'info' });
                    setUndoable({ assignment: a, message });
                    reload();
                })
                .catch((err: unknown) => {
                    setToast({ message: err instanceof Error ? err.message : 'Could not remove', severity: 'error' });
                })
                .finally(() => {
                    setSaving(false);
                });
        },
        [reload, staffById],
    );

    /** Put a removed assignment back. It comes back with a new id, which nothing holds on to. */
    const handleUndoRemove = useCallback(() => {
        const a = undoable?.assignment ?? null;
        setUndoable(null);
        setToast(null);
        if (a === null) {
            return;
        }
        setSaving(true);
        apiFetch<{ inserted: number; skipped: number }>('/calendar/assignments', {
            method: 'POST',
            body: JSON.stringify({
                eventId: a.eventId,
                staffIds: [a.staffId],
                dates: [a.date],
                startTime: a.startTime,
                endTime: a.endTime,
            }),
        })
            .then(() => {
                setToast({ message: 'Restored', severity: 'success' });
                reload();
            })
            .catch((err: unknown) => {
                setToast({ message: err instanceof Error ? err.message : 'Could not restore', severity: 'error' });
            })
            .finally(() => {
                setSaving(false);
            });
    }, [undoable, reload]);

    /**
     * Drag a staff chip to another job or day.
     *
     * A 409 from the API means the unique index refused it — that person is
     * already on that job that day — which is a sentence, not a failure.
     */
    const handleMoveAssignment = useCallback(
        (a: CalendarAssignment, toEventId: number, toDate: IsoDate) => {
            setSaving(true);
            apiFetch<unknown>(`/calendar/assignments/${String(a.id)}`, {
                method: 'PATCH',
                body: JSON.stringify({ date: toDate, eventId: toEventId }),
            })
                .then(() => {
                    setToast({ message: 'Moved', severity: 'success' });
                    reload();
                })
                .catch((err: unknown) => {
                    setToast({ message: err instanceof Error ? err.message : 'Could not move', severity: 'error' });
                })
                .finally(() => {
                    setSaving(false);
                });
        },
        [reload],
    );

    const handleSaveEvent = useCallback(
        (draft: EventDraft) => {
            setSaving(true);
            setDialogError(null);
            const path = editing === null ? '/calendar/events' : `/calendar/events/${String(editing.id)}`;
            const { carryCrewTo, carryCrewIds, ...fields } = draft;

            async function run(): Promise<string> {
                const saved = await apiFetch<CalendarEvent>(path, {
                    method: editing === null ? 'POST' : 'PATCH',
                    body: JSON.stringify(fields),
                });

                // The crew carry-over is a second call on purpose: the dates are
                // saved either way, so a failure here cannot lose the edit the
                // user actually asked for.
                if (carryCrewTo.length === 0 || carryCrewIds.length === 0) {
                    return editing === null ? 'Job created' : 'Job saved';
                }

                const result = await apiFetch<{ inserted: number; skipped: number }>(
                    '/calendar/assignments',
                    {
                        method: 'POST',
                        body: JSON.stringify({
                            eventId: saved.id,
                            staffIds: carryCrewIds,
                            dates: carryCrewTo,
                        }),
                    },
                );
                return result.skipped > 0
                    ? `Job extended · added ${String(result.inserted)}, skipped ${String(result.skipped)} already rostered`
                    : `Job extended · added ${String(result.inserted)} to the crew`;
            }

            run()
                .then((message) => {
                    setToast({ message, severity: 'success' });
                    setDialogOpen(false);
                    reload();
                })
                .catch((err: unknown) => {
                    setDialogError(err instanceof Error ? err.message : 'Could not save');
                })
                .finally(() => {
                    setSaving(false);
                });
        },
        [editing, reload],
    );

    const handleArchiveEvent = useCallback(
        (event: CalendarEvent, archived: boolean) => {
            setSaving(true);
            apiFetch<unknown>(`/calendar/events/${String(event.id)}/archive`, {
                method: 'PATCH',
                body: JSON.stringify({ archived }),
            })
                .then(() => {
                    setToast({ message: archived ? 'Job archived' : 'Job restored', severity: 'success' });
                    setDialogOpen(false);
                    reload();
                })
                .catch((err: unknown) => {
                    setDialogError(err instanceof Error ? err.message : 'Could not archive');
                })
                .finally(() => {
                    setSaving(false);
                });
        },
        [reload],
    );

    const handleDeleteEvent = useCallback(
        (event: CalendarEvent) => {
            setSaving(true);
            apiFetch<unknown>(`/calendar/events/${String(event.id)}`, { method: 'DELETE' })
                .then(() => {
                    setToast({ message: 'Job deleted', severity: 'success' });
                    setDialogOpen(false);
                    reload();
                })
                .catch((err: unknown) => {
                    setDialogError(err instanceof Error ? err.message : 'Could not delete');
                })
                .finally(() => {
                    setSaving(false);
                });
        },
        [reload],
    );

    const openNewEvent = useCallback(() => {
        setEditing(null);
        setDialogError(null);
        setDialogOpen(true);
    }, []);

    const openEditEvent = useCallback((event: CalendarEvent) => {
        setEditing(event);
        setDialogError(null);
        setDialogOpen(true);
    }, []);

    /** The "+ Staff" affordance inside a cell: select just that cell and open the picker. */
    const handleAddStaff = useCallback((eventId: number, date: IsoDate) => {
        setSelection(new Set([eventCellKey(eventId, date)]));
        setSaveError(null);
        setPickerOpen(true);
    }, []);

    const clashCount = useMemo(
        () => Object.values(range?.clashes ?? {}).reduce((n, days) => n + days.length, 0),
        [range],
    );

    return (
        <Container maxWidth={false} sx={{ pt: 2.5, pb: 4, px: { xs: 1.5, md: 3 } }}>
            {/* ─── Toolbar ────────────────────────────────────────────────── */}
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', minWidth: 150, width: { xs: '100%', sm: 'auto' } }}>
                    {span === 'month' ? formatMonthYear(from) : `${formatShort(from)} – ${formatShort(to)}`}
                </Typography>

                <ButtonGroup size="small" variant="outlined">
                    <Button
                        onClick={() => {
                            step(-1);
                        }}
                        aria-label="Previous"
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </Button>
                    <Button
                        onClick={() => {
                            setCursor(today);
                            setOverride(null);
                            setSelection(new Set());
                        }}
                        startIcon={<TodayIcon fontSize="small" />}
                    >
                        Today
                    </Button>
                    <Button
                        onClick={() => {
                            step(1);
                        }}
                        aria-label="Next"
                    >
                        <ChevronRightIcon fontSize="small" />
                    </Button>
                </ButtonGroup>

                <ButtonGroup size="small">
                    {SPANS.map((s) => (
                        <Button
                            key={s.id}
                            variant={span === s.id ? 'contained' : 'outlined'}
                            onClick={() => {
                                setSpan(s.id);
                                setOverride(null);
                                setSelection(new Set());
                            }}
                        >
                            {s.label}
                        </Button>
                    ))}
                </ButtonGroup>

                <TextField
                    size="small"
                    placeholder="Filter jobs"
                    value={filter}
                    onChange={(e) => {
                        setFilter(e.target.value);
                    }}
                    sx={{ width: { xs: 'auto', sm: 170 }, flex: { xs: 1, sm: 'none' }, minWidth: 0 }}
                />

                {readOnly ? null : (
                    <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openNewEvent}>
                        New job
                    </Button>
                )}

                <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />

                <Chip size="small" variant="outlined" label={`${String(visibleEvents.length)} jobs`} />

                {clashCount > 0 ? (
                    <Tooltip title="Staff booked on more than one job in a day. Normal on split days — shown, never blocked.">
                        <Badge badgeContent={clashCount} color="warning" max={99}>
                            <Chip size="small" label="Multi-job days" variant="outlined" />
                        </Badge>
                    </Tooltip>
                ) : null}

                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={showArchived}
                            onChange={(e) => {
                                setShowArchived(e.target.checked);
                            }}
                        />
                    }
                    label={
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <Inventory2Icon sx={{ fontSize: 15 }} />
                            <Typography sx={{ fontSize: '0.78rem' }}>Archived</Typography>
                        </Stack>
                    }
                    sx={{ mr: 0 }}
                />

                {loading && !initialising ? <CircularProgress size={18} /> : null}
            </Stack>

            {/* The legacy toolbar's eight date buttons, computed rather than
                built with string maths on d/m/Y as the originals were. */}
            {/* One swipeable line on a phone rather than three rows of buttons
                pushing the grid down the page. */}
            <Stack
                direction="row"
                gap={0.75}
                sx={{
                    mb: 2,
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    overflowX: { xs: 'auto', sm: 'visible' },
                    pb: { xs: 0.5, sm: 0 },
                }}
            >
                {QUICK_RANGES.map((q) => {
                    const r = q.of(today);
                    const active = from === r.from && to === r.to;
                    return (
                        <Button
                            key={q.id}
                            size="small"
                            variant={active ? 'contained' : 'outlined'}
                            onClick={() => {
                                setSelection(new Set());
                                setOverride(r);
                            }}
                            sx={{ fontSize: '0.72rem', py: 0.25, flexShrink: 0, whiteSpace: 'nowrap' }}
                        >
                            {q.label}
                        </Button>
                    );
                })}
            </Stack>

            {initialising ? <LinearProgress sx={{ mb: 2 }} /> : null}

            {error !== null ? (
                <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={reload}>Retry</Button>}>
                    {error}
                </Alert>
            ) : null}

            {range?.truncated === true ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    This range has more rows than the calendar will draw at once. Narrow the span.
                </Alert>
            ) : null}

            {!initialising && visibleEvents.length === 0 ? (
                <Alert
                    severity="info"
                    action={
                        filter.trim() === '' && !readOnly ? (
                            <Button size="small" onClick={openNewEvent}>
                                New job
                            </Button>
                        ) : null
                    }
                >
                    {filter.trim() === '' ? 'No jobs scheduled in this range.' : `No jobs match "${filter}".`}
                </Alert>
            ) : null}

            {/* ─── Grid ───────────────────────────────────────────────────── */}
            {initialising || visibleEvents.length === 0 ? null : (
                <JobGrid
                    dates={dates}
                    events={visibleEvents}
                    cells={cells}
                    staffById={staffById}
                    categoryNames={categoryNames}
                    clashes={range?.clashes ?? {}}
                    selection={selection}
                    onSelectionChange={setSelection}
                    onAddStaff={handleAddStaff}
                    onRemoveAssignment={handleRemoveAssignment}
                    onMoveAssignment={handleMoveAssignment}
                    onEditEvent={openEditEvent}
                    today={today}
                    readOnly={readOnly}
                />
            )}

            <Typography sx={{ mt: 1, fontSize: '0.72rem', color: 'text.disabled' }}>
                {readOnly ? (
                    <>View only on a phone · open the calendar on a computer or tablet to make changes</>
                ) : (
                    <>
                        Drag across a job&apos;s row to pick days · drag a name to another day or job to move them ·
                        hover a name and click × to take them off · click a job to edit it · Esc to clear
                    </>
                )}
            </Typography>

            {/* ─── Selection action bar ───────────────────────────────────── */}
            {selection.size > 0 && !pickerOpen && !readOnly ? (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: { xs: 16, sm: 24 },
                        // Pinned to both edges on a phone, so a long site address
                        // truncates instead of pushing the buttons off-screen.
                        left: { xs: 12, sm: '50%' },
                        right: { xs: 12, sm: 'auto' },
                        transform: { xs: 'none', sm: 'translateX(-50%)' },
                        zIndex: 1200,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1.25,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'secondary.main',
                        boxShadow: 6,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            flex: { xs: 1, sm: 'none' },
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {target === null
                            ? `${String(selection.size)} selected`
                            : `${String(target.dates.length)} ${target.dates.length === 1 ? 'day' : 'days'} on ${(target.event.siteAddress ?? '').trim() === '' ? (target.event.jobNumber ?? 'this job') : String(target.event.siteAddress)}`}
                    </Typography>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                            setSaveError(null);
                            setPickerOpen(true);
                        }}
                    >
                        Edit staff
                    </Button>
                    <Button
                        size="small"
                        onClick={() => {
                            setSelection(new Set());
                        }}
                    >
                        Clear
                    </Button>
                </Box>
            ) : null}

            <StaffPicker
                open={pickerOpen}
                target={target}
                staff={staff}
                saving={saving}
                error={saveError}
                onClose={() => {
                    setPickerOpen(false);
                }}
                onSubmit={handleSubmit}
            />

            <EventDialog
                open={dialogOpen}
                event={editing}
                categories={categories}
                defaultRange={{ from, to }}
                saving={saving}
                error={dialogError}
                onClose={() => {
                    setDialogOpen(false);
                }}
                onSave={handleSaveEvent}
                onArchive={handleArchiveEvent}
                onDelete={handleDeleteEvent}
            />

            <Toast
                toast={toast}
                onClose={(reason) => {
                    // A click elsewhere on the board would otherwise dismiss the
                    // Undo before anyone could reach it.
                    if (reason === 'clickaway' && canUndo) {
                        return;
                    }
                    setToast(null);
                    setUndoable(null);
                }}
                action={
                    !canUndo ? undefined : (
                        <Button size="small" variant="outlined" onClick={handleUndoRemove} sx={{ minWidth: 0, px: 1.25 }}>
                            Undo
                        </Button>
                    )
                }
            />
        </Container>
    );
}
